import { useEffect, useState } from "react";
import Decimal from "decimal.js";
import "./OptionsCalculator.css"



const getEmptyOption = () => ({ type: "CALL" as "CALL" | "PUT", strike: "", strikeCurrency: "USD", premium: "", premiumCurrency: "EUR", id: crypto.randomUUID() });

function OptionsCalculatorPage() {
    const [pageState, setPageState] = useState({
        options: [getEmptyOption()] as { type: "CALL" | "PUT", strike: string, strikeCurrency: string, premium: string, premiumCurrency: string, id: string }[],
        investment: "1000",
        investmentCurrency: "EUR",

        targetPriceMin: 1,
        targetPriceMax: 100,
        targetPrice: 50,
        targetPriceCurrency: "USD",

        displayedResults: [] as {
            strike: string;
            premium: string;
            breakeven: string;
            profit: string;
            _profit: number;
            highestProfit: boolean;
        }[],

        breakevenCurrency: "STRIKE" as CurrencyCode | "STRIKE" | "PREMIUM",
    });

    useEffect(() => {
        let lowest = Number.MAX_SAFE_INTEGER;
        let highest = Number.MIN_SAFE_INTEGER;

        pageState.options.forEach(opt => {
            const strike = Number(opt.strike);
            if (opt.strike !== "" && !isNaN(strike)) {
                if (strike > highest)
                    highest = strike;

                if (strike < lowest)
                    lowest = strike;
            }
        });

        if (lowest === Number.MAX_SAFE_INTEGER || highest === Number.MIN_SAFE_INTEGER)
            return;

        setPageState(state => ({ ...state, targetPriceMin: +(lowest / 3).toFixed(2), targetPriceMax: +(highest * 3).toFixed(2) }))
    }, [pageState.options]);

    useEffect(() => {
        const optionsValid = pageState.options.every(opt => opt.premium !== "" && !isNaN(Number(opt.premium)) && opt.strike !== "" && !isNaN(Number(opt.strike)));
        const investmentValid = pageState.investment !== "" && !isNaN(Number(pageState.investment));
        const targetPriceValid = pageState.targetPrice > 0;

        if (optionsValid && investmentValid && targetPriceValid) {
            const mappedOptions = pageState.options.map(opt => new Option(opt.type, new CurrencyValue(opt.strikeCurrency as CurrencyCode, new Decimal(opt.strike)), new CurrencyValue(opt.premiumCurrency as CurrencyCode, new Decimal(opt.premium))));
            const results = mappedOptions.map(option => {
                const breakeven = option.getBreakeven();
                const profit = option.getProfit(new CurrencyValue(pageState.investmentCurrency as CurrencyCode, new Decimal(pageState.investment)), new CurrencyValue(pageState.targetPriceCurrency as CurrencyCode, new Decimal(pageState.targetPrice)))

                const targetBreakevenCurrency = pageState.breakevenCurrency === "STRIKE" ? option.currency : (pageState.breakevenCurrency === "PREMIUM" ? option.premiumInSourceCurrency.currency : pageState.breakevenCurrency)

                return {
                    strike: option.strike.toFormatString(),
                    premium: option.premiumInSourceCurrency.toFormatString(),
                    breakeven: breakeven.convert(targetBreakevenCurrency).toFormatString(),
                    profit: profit.convert(pageState.investmentCurrency as CurrencyCode).toFormatString(),
                    _profit: profit.convert(pageState.investmentCurrency as CurrencyCode).amount.toNumber(),
                    highestProfit: false
                }
            });

            let highestProfit = null as number | null;
            results.forEach(res => {
                if (highestProfit === null || res._profit >= highestProfit)
                    highestProfit = res._profit;
            });

            results.filter(r => r._profit === highestProfit).forEach(r => r.highestProfit = true);

            setPageState(state => ({ ...state, displayedResults: results }))
        }
    }, [pageState.breakevenCurrency, pageState.investment, pageState.investmentCurrency, pageState.options, pageState.targetPrice, pageState.targetPriceCurrency]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const shareCode = params.get("shareCode");
        window.history.replaceState(null, '', window.location.pathname);
        if (!shareCode)
            return;

        const decodedSharedData = JSON.parse(atob(shareCode));

        setPageState(state => ({
            ...state,
            ...decodedSharedData
        }))
    }, []);

    const optionsValid = pageState.options.every(opt => opt.premium !== "" && !isNaN(Number(opt.premium)) && opt.strike !== "" && !isNaN(Number(opt.strike)));
    const investmentValid = pageState.investment !== "" && !isNaN(Number(pageState.investment));
    const targetPriceValid = pageState.targetPrice > 0;

    return (
        <div id="options-calculator-page">
            <div id="options-container">
                {pageState.options.map((opt, i) => {
                    return (
                        <div className="option-input-row" key={`${opt.id}`}>
                            <select value={pageState.options[i].type} onChange={e => setPageState(pageState => {
                                const newState = structuredClone(pageState);
                                newState.options[i].type = e.target.value as any;
                                return newState;
                            })}>
                                <option value={"CALL"}>Call</option>
                                <option value={"PUT"}>Put</option>
                            </select>
                            <label>Strike</label>
                            <input type="text" value={pageState.options[i].strike} onChange={e => setPageState(pageState => {
                                const newState = structuredClone(pageState);
                                newState.options[i].strike = e.target.value.replaceAll(",", ".");
                                return newState;
                            })} />
                            <select value={pageState.options[i].strikeCurrency} onChange={e => setPageState(pageState => {
                                const newState = structuredClone(pageState);
                                newState.options[i].strikeCurrency = e.target.value;
                                return newState;
                            })}>
                                <option value={"USD"}>USD</option>
                                <option value={"EUR"}>EUR</option>
                            </select>
                            <label>Premium</label>
                            <input type="text" value={pageState.options[i].premium} onChange={e => setPageState(pageState => {
                                const newState = structuredClone(pageState);
                                newState.options[i].premium = e.target.value.replaceAll(",", ".");
                                return newState;
                            })} />
                            <select value={pageState.options[i].premiumCurrency} onChange={e => setPageState(pageState => {
                                const newState = structuredClone(pageState);
                                newState.options[i].premiumCurrency = e.target.value;
                                return newState;
                            })}>
                                <option value={"USD"}>USD</option>
                                <option value={"EUR"}>EUR</option>
                            </select>
                        </div>
                    );
                })}
                <input type="button" value="Add Option" onClick={() => setPageState(state => ({ ...state, options: [...state.options, getEmptyOption()] }))} />
                <input type="button" value="Reset Options" onClick={() => setPageState(state => ({ ...state, options: [getEmptyOption()], displayedResults: [] }))} />
                <input type="button" disabled={!optionsValid || !investmentValid || !targetPriceValid} value="Copy Share URL" onClick={() => {
                    const b64ShareCode = btoa(JSON.stringify({
                        options: pageState.options,
                        investment: pageState.investment,
                        investmentCurrency: pageState.investmentCurrency,
                        targetPrice: pageState.targetPrice,
                        targetPriceCurrency: pageState.targetPriceCurrency
                    }));

                    const params = new URLSearchParams({
                        "shareCode": b64ShareCode
                    }).toString();

                    const shareUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${params}`;
                    navigator.clipboard.writeText(shareUrl);
                }} />
                <div>
                    <label>Breakeven Currency</label>
                    <select value={pageState.breakevenCurrency} onChange={e => setPageState(pageState => {
                        const newState = structuredClone(pageState);
                        newState.breakevenCurrency = e.target.value as any;
                        return newState;
                    })}>
                        <option value={"USD"}>USD</option>
                        <option value={"EUR"}>EUR</option>
                        <option value="STRIKE">Same as Strike</option>
                        <option value="PREMIUM">Same as Premium</option>
                    </select>
                </div>
                <div>
                    <label>Investment</label>
                    <input type="text" value={pageState.investment} onChange={e => setPageState(pageState => {
                        const newState = structuredClone(pageState);
                        newState.investment = e.target.value.replaceAll(",", ".");
                        return newState;
                    })} />
                    <select value={pageState.investmentCurrency} onChange={e => setPageState(pageState => {
                        const newState = structuredClone(pageState);
                        newState.investmentCurrency = e.target.value;
                        return newState;
                    })}>
                        <option value={"USD"}>USD</option>
                        <option value={"EUR"}>EUR</option>
                    </select>
                </div>
                <div>
                    <label>Target Price</label>
                    <input type="number" value={pageState.targetPrice} onChange={ev => {
                        const value = ev.target.value.replaceAll(",", ".");
                        const num = Number(value);
                        if (value && !isNaN(num) && num >= 0) {
                            setPageState(state => ({ ...state, targetPrice: num }))
                        }
                    }} />
                    <select value={pageState.targetPriceCurrency} onChange={e => setPageState(pageState => {
                        const newState = structuredClone(pageState);
                        newState.targetPriceCurrency = e.target.value;
                        return newState;
                    })}>
                        <option value={"USD"}>USD</option>
                        <option value={"EUR"}>EUR</option>
                    </select>
                </div>
                <input type="range" min={pageState.targetPriceMin} max={pageState.targetPriceMax} value={pageState.targetPrice} step="0.1" onChange={e => setPageState(state => ({ ...state, targetPrice: Number(e.target.value) }))} />
            </div>
            <div id="results-container">
                <table>
                    <thead>
                        <tr>
                            <th>Strike</th>
                            <th>Premium</th>
                            <th>Breakeven</th>
                            <th>Net Profit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageState.displayedResults.map(res => (
                            <tr key={`${res.profit}|${res.strike}|${res.premium}|${res.breakeven}|`} className={`${res.highestProfit ? "highest-profit " : ""}${res._profit >= 0 ? "profit" : "loss"}`}>
                                <td>{res.strike}</td>
                                <td>{res.premium}</td>
                                <td>{res.breakeven}</td>
                                <td>{res.profit}</td>
                            </tr>
                        ))}

                    </tbody>
                </table>
            </div>
        </div>
    );
}

export { OptionsCalculatorPage }


export type CurrencyCode = "EUR" | "USD"

export class CurrencyValue {
    constructor(public readonly currency: CurrencyCode, public readonly amount: Decimal) { }

    add(amount: CurrencyValue) {
        const convertedAmount = amount.convert(this.currency);
        return new CurrencyValue(this.currency, this.amount.add(convertedAmount.amount));
    }

    negate() {
        return new CurrencyValue(this.currency, this.amount.negated())
    }

    convert(target: CurrencyCode) {
        if (this.currency === target)
            return this;

        return new CurrencyValue(target, this.amount.times(getConversionRate(this.currency, target)))
    }

    toFormatString() {
        return `${this.amount.toFixed(2)} ${this.currency}`
    }
}

export type OptionType = "CALL" | "PUT"

export class Option {
    public readonly currency: CurrencyCode
    public readonly strike: CurrencyValue
    public readonly premium: CurrencyValue
    public readonly premiumInSourceCurrency: CurrencyValue

    constructor(public readonly type: OptionType, strike: CurrencyValue, premium: CurrencyValue) {
        this.currency = strike.currency;
        this.strike = strike;
        this.premium = premium.convert(this.currency);
        this.premiumInSourceCurrency = premium;
    }

    getBreakeven() {
        if (this.type === "CALL") {
            return this.strike.add(this.premium);
        } else {
            return this.strike.add(this.premium.negate());
        }
    }

    getPurchasableOptions(investment: CurrencyValue) {
        const convertedInvestment = investment.convert(this.currency);
        return convertedInvestment.amount.dividedBy(this.premium.amount).floor();
    }

    getProfit(investment: CurrencyValue, stockPriceOnExpiration: CurrencyValue) {
        const convertedInvestment = investment.convert(this.currency);
        const convertedStockPrice = stockPriceOnExpiration.convert(this.currency);

        const purchasableOptions = this.getPurchasableOptions(investment);

        if (this.type === "CALL") {
            if (this.strike.amount.greaterThanOrEqualTo(convertedStockPrice.amount))
                return convertedInvestment.negate();

            const profitPerOption = convertedStockPrice.amount.sub(this.strike.amount);
            const totalProfit = convertedInvestment.amount.neg().add(profitPerOption.times(purchasableOptions));

            return new CurrencyValue(this.currency, totalProfit);
        } else {
            if (this.strike.amount.lessThanOrEqualTo(convertedStockPrice.amount))
                return convertedInvestment.negate();

            const profitPerOption = this.strike.amount.sub(convertedStockPrice.amount);
            const totalProfit = convertedInvestment.amount.neg().add(profitPerOption.times(purchasableOptions));

            return new CurrencyValue(this.currency, totalProfit);
        }
    }
}

const CONVERSION_RATES: Record<string, Record<string, Decimal>> = {
    "EUR": {
        "USD": new Decimal(1.1)
    },

    "USD": {
        "EUR": new Decimal(1).dividedBy(1.1)
    }
}

function getConversionRate(from: CurrencyCode, to: CurrencyCode) {
    return CONVERSION_RATES[from][to];
}

